/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable guard-for-in */
/* eslint-disable no-restricted-syntax */
/* eslint-disable react/no-danger */
import { clearSelection } from "action/garage/clearSelection";
import AmpService from "analytics/amplitude/amplitudeEvents";
import { IAutoCompletePrefix } from "api/interfaces/search.type";
import useOutsideClick from "hooks-contexts/useOutsideClick";
import { RootState } from "interfaces/generics.type";
import SearchModal from "modules/search/SearchModal";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import {
  useCallback, useEffect, useRef, useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { SearchIcon } from "resources/svg-images/SearchIcon";
import HideIf from "util/HideIf";
import { trackEvents } from "util/analytics/trackEvents";
import { debounceWithCallAPI } from "util/lodash/debounceWithCallAPI";
import { isEmpty } from "util/lodash/isEmpty";
import autocomplete from "util/search/autoComplete";
import { getDynamicUrlPrefixSearch } from "util/search/getDynamicUrlPrefixSearch";
import { equalVehicles } from "util/vehicle/equal-vehicles";
import { validateVehicle } from "util/vehicle/validate-vehicle";
import { amplitudeVehiclePayloadChanger } from "util/vehicle/vehicle-helper";
import styles from "./header.module.scss";

const SearchModalSuggestions = dynamic(() => import(/* webpackChunkName: "searchModal-suggestions" */ "modules/search/SearchModalSuggestions"),
  { ssr: false });

function HeaderAutoComplete(props: any) {
  const router = useRouter();
  const [value, setValue] = useState("");
  const { t } = useTranslation("common");
  const { vehicleDropdown, className, searchInputClassName } = props;
  const { selectedVehicle } = useSelector((state: RootState) => state.garage);

  const [isOpen, setIsOpen] = useState(false);
  const [autoCompleteVehicle, setAutoCompleteVehicle] = useState(null);

  const searchMainDivRef = useRef(null);
  const searchBarRef = useRef(null);
  const searchModalSuggestionsRef = useRef(null);

  const { currentPageType } = useSelector((state: RootState) => state.utils);
  const [suggestions, setSuggestions] = useState<IAutoCompletePrefix[]>([]);
  const [showLoaderOnType, setShowLoaderOnType] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [responseAutoComplete, setResponseAutoComplete] = useState([]);
  const [showPredictiveDropdown, setShowPredictiveDropdown] = useState(true);
  const dispatch = useDispatch();

  const searchWithVehicle = Boolean(autoCompleteVehicle || selectedVehicle);

  const debounceOnSuggestionsFetchRequested = useCallback(
    debounceWithCallAPI(autocomplete, 300, handleAutoComplete, setShowLoaderOnType, true),
    [],
  );

  function handleAutoComplete(res: any) {
    setResponseAutoComplete(res.data);

    if (res.vehicle) {
      if (Object.values(res.vehicle).some((v) => v != null)) {
        setAutoCompleteVehicle(res.vehicle);
      } else {
        setAutoCompleteVehicle(null);
      }
    }
  }

  function toggleDropDown() {
    setIsOpen(false);
  }

  useEffect(() => {
    router.events.on("routeChangeComplete", toggleDropDown);
    return () => {
      router.events.off("routeChangeComplete", toggleDropDown);
    };
  }, []);

  useEffect(() => {
    if (value.length === 0) {
      setShowPredictiveDropdown(true);
      setResponseAutoComplete([]);
      setSuggestions([]);
    } else setShowPredictiveDropdown(false);
  }, [value]);

  function fetchSearchPhrase(suggestionsList?: IAutoCompletePrefix[], vehicle?: any) {
    setShowLoader(true);

    const activeSuggestions = suggestionsList || suggestions;
    const activeVehicle = vehicle || autoCompleteVehicle || selectedVehicle;

    if (!activeSuggestions || activeSuggestions.length === 0) {
      router.push(`/search?ph=${value}`).then(() => setShowLoader(false));
      return;
    }

    // Suggestions can be a list or a single object passed from <SearchModalSuggestions />
    const suggestionData = Array.isArray(activeSuggestions) ? activeSuggestions.find((eachData) => searchBarRef.current.innerHTML === eachData.value) : activeSuggestions;

    const equalSearchandCurrentVehicles = equalVehicles(vehicle, selectedVehicle);

    if (!equalSearchandCurrentVehicles && selectedVehicle) {
      dispatch(clearSelection());
    }
    if (suggestionData) {
      getDynamicUrlPrefixSearch(suggestionData, activeVehicle, router, () => setShowLoader(false));
    } else {
      getDynamicUrlPrefixSearch(activeSuggestions[0], activeVehicle, router, () => setShowLoader(false));
    }
  }

  function searchIconClicked() {
    if (!isEmpty(value) && showLoaderOnType === false) {
      trackEvents("search_click", "search", {
        inputText: value,
      });
      AmpService.searchEvent("search_click", {
        inputText: value,
        selectedVehicle: amplitudeVehiclePayloadChanger(selectedVehicle) || amplitudeVehiclePayloadChanger(null),
        searchContextSelectedVehicle: searchWithVehicle ? amplitudeVehiclePayloadChanger(autoCompleteVehicle || selectedVehicle) : amplitudeVehiclePayloadChanger(null),
        sourcePageType: currentPageType || "NOT_PRESENT",
      });
      fetchSearchPhrase();
    } else {
      toggleDropDown();
    }
  }

  useEffect(() => {
    setShowLoaderOnType(false);

    if (responseAutoComplete.length > 0) {
      setSuggestions(responseAutoComplete);
    } else {
      setSuggestions([]);
    }
  }, [responseAutoComplete]);

  async function search(event) {
    if (event.keyCode === 13 && !isEmpty(value)) {
      event.preventDefault();
      debounceOnSuggestionsFetchRequested.cancel();
      setShowLoaderOnType(true);

      try {
        const response = await autocomplete(value);
        if (response?.data) {
          let vehicle = null;

          if (response.vehicle) {
            if (Object.values(response.vehicle).some((v) => v != null)) {
              vehicle = response.vehicle;
            } else {
              vehicle = selectedVehicle;
            }
          }
          fetchSearchPhrase(response.data, vehicle);
        } else {
          fetchSearchPhrase();
        }
      } catch (error) {
        fetchSearchPhrase();
      } finally {
        setShowLoaderOnType(false);
      }

      trackEvents("search_enter", "search", {
        inputText: value,
      });
      AmpService.searchEvent("search_enter", {
        inputText: value,
        selectedVehicle: amplitudeVehiclePayloadChanger(selectedVehicle) || amplitudeVehiclePayloadChanger(null),
        searchContextSelectedVehicle: searchWithVehicle ? amplitudeVehiclePayloadChanger(autoCompleteVehicle || selectedVehicle) : amplitudeVehiclePayloadChanger(null),
        sourcePageType: currentPageType || "NOT_PRESENT",
      });
    } else if (event.keyCode === 27) {
      toggleDropDown();
      searchBarRef.current?.blur();
    }
  }

  function changeText(event) {
    let updatedInput = event.target.value || event.target.innerHTML || "";
    updatedInput = updatedInput.replace(/&nbsp;/g, " ");
    setValue(updatedInput);
    if (updatedInput) {
      debounceOnSuggestionsFetchRequested(updatedInput);
    }
  }

  function focusInput() {
    if (searchBarRef.current) {
      if (!isOpen) {
        setIsOpen(true);
      }
    }
  }

  function onInputFocus(e: any) {
    e.preventDefault();
  }

  useOutsideClick(searchModalSuggestionsRef, toggleDropDown);

  return (
    <>
      {isOpen && (
        <div className={styles.overlay} />
      )}

      <div
        ref={searchModalSuggestionsRef}
        className={`${styles.search_input_container} ${className}`}
      >
        <div
          className={`${styles.header_d} ${searchInputClassName} ${isOpen ? styles.open_search_bar : ""}`}
          id="search-modal"
        >

          <div className={styles.autosuggest_container} data-clarity-unmask="true">
            <HideIf show={vehicleDropdown}>
              <div
                data-tooltip={isOpen && !validateVehicle(selectedVehicle) ? t("Please select a vehicle to ensure part fit") : ""}
                className={isOpen && validateVehicle(selectedVehicle) ? styles.all_vehicle_dropdown : ""}
              >
                {vehicleDropdown}
              </div>
            </HideIf>
            <div
              style={{
                width: "100%",
                overflow: "hidden",
                display: "flex",
              }}
              onFocus={focusInput}
            >
              <SearchModal
                value={value}
                setValue={setValue}
                searchMainDivRef={searchMainDivRef}
                onKeyDown={search}
                changeText={changeText}
                searchBarRef={searchBarRef}
              />
              <div onClick={onInputFocus} className={styles.search_icon_container}>
                <div onClick={searchIconClicked} className={`${styles.search_icon} `}>
                  <SearchIcon />
                </div>
              </div>
            </div>

          </div>

        </div>
        {isOpen && (

          <SearchModalSuggestions
            suggestions={suggestions}
            showPredictiveDropdown={showPredictiveDropdown}
            setShowPredictiveDropdown={setShowPredictiveDropdown}
            setValue={setValue}
            searchBarRef={searchBarRef}
            setShowLoader={setShowLoader}
            showLoader={showLoader}
            autoCompleteVehicle={autoCompleteVehicle}
            fetchSearchPhrase={fetchSearchPhrase}
          />

        )}
      </div>
    </>
  );
}

export default HeaderAutoComplete;
